/* eslint-disable max-lines */
import { computed, effect, inject, Signal } from "@angular/core";
import { rxResource } from "@angular/core/rxjs-interop";
import {
  DepartRetraiteUtils,
  Moment,
  PreconisationInformationDTO,
  ResultatsCalculDTO,
  Simulation,
  TypeModalite
} from "@mra/hlp9002";
import { tapResponse } from "@ngrx/operators";
import { getState, patchState, signalStore, withComputed, withHooks, withMethods, withState } from "@ngrx/signals";
import { filter, Observable, of, switchMap } from "rxjs";
import { Beneficiaire } from "src/app/models/beneficiaire.model";
import { PreconisationModifiee } from "src/app/models/preconisationModifiee.model";
import { RenteCalculService } from "src/app/shared/services/rente/rente-calcul.service";
import { LocalStorageService, StateKey } from "../shared/services/local-storage.service";
import { PrecoInformationsService } from "../shared/services/preconisation/preco-informations.service";
import { TokenService } from "../shared/services/user/token.service";
import { SimuUtils } from "../utils/simu-utils";
import { errorsStore } from "./errors.store";
import { questionnaireStore } from "./questionnaire.store";
import { ChangementSimConso, EntreeHistoriqueOption, RenteState, SavedRenteState, SousChampConso } from "./state";
import { userStore } from "./user.store";

const LIMITE_HISTORIQUE: number = 500;

function createDefaultState(): RenteState {
  return {
    renteSansOpt: null,
    renteAvecOpt: null,

    preconisation: null,
    precoModifiee: null,
    historiqueConso: [],

    selectedOptionsRente: new Map<TypeModalite, string>(),
    historiqueOptions: [],

    beneficiaire: null,
    clauseBeneficiaireCode: null
  };
}

export type RenteSansOptSignalData = {
  dateEffet?: string;
  consoCapital?: number;
  renteSansOpt?: ResultatsCalculDTO;
};

export const renteStore = signalStore(
  { providedIn: "root" },
  withState(createDefaultState()),

  withComputed((store) => {
    const user = inject(userStore);

    return {
      montantRenteAvecOpt: computed(() => store.renteAvecOpt()?.montantTotal),
      precoCapital: computed(() => store.preconisation()?.sortieCapital),
      modalitesPreconisees: computed(() => store.preconisation()?.renteModalites ?? []),
      codesModalites: computed(() => {
        if (store.selectedOptionsRente().size === 0) {
          return store.preconisation()?.renteModalites?.map((mod) => mod.code) ?? ([] as string[]);
        }
        return Array.from(store.selectedOptionsRente()?.entries()).map((entry) => entry[1]);
      }),

      consommationRente: computed(() => {
        return store.precoModifiee()?.consommationRente ?? user.epargneCumulee() ?? 0;
      }),

      consommationCapital: computed(() => {
        return store.precoModifiee()?.consommationCapital ?? 0;
      }),

      consommationRpp: computed(() => {
        return store.precoModifiee()?.consommationRpp ?? 0;
      })
    };
  }),

  withMethods(
    (
      store,
      renteService = inject(RenteCalculService),
      preconisationService = inject(PrecoInformationsService),
      errorStore = inject(errorsStore),
      user = inject(userStore),
      questionnaire = inject(questionnaireStore),
      tokenService = inject(TokenService)
    ) => {
      function dateEffet() {
        const configUti: PreconisationModifiee = store.precoModifiee();
        const simu: Simulation = user.simulation();
        if (!simu) return null;
        return SimuUtils.getDateEffet(configUti, simu);
      }
      return {
        unsetSimulation(): void {
          patchState(store, () => createDefaultState());
        },

        retrieveState(savedState: SavedRenteState): void {
          patchState(store, () => {
            if (savedState) {
              savedState.selectedOptionsRente = new Map(Object.entries(savedState.selectedOptionsRente));
            }
            return savedState ?? createDefaultState();
          });
        },

        dateEffet: computed(() => dateEffet()),

        getPreconisationModifiee: computed(() => {
          const existing: PreconisationModifiee = store.precoModifiee();
          if (existing) return existing;
          const simu: Simulation = user.simulation();
          const dateDepartRetraite: Moment = new DepartRetraiteUtils().calculDateDepartRetraite(
            simu.affilie.naissance.date
          );
          const originalPreco: PreconisationInformationDTO = store?.preconisation();
          return originalPreco
            ? {
                ...originalPreco,
                dateEffet: dateDepartRetraite.format("DD/MM/YYYY"),
                renteRetenuOption: 0
              }
            : null;
        }),

        selectionnerOption(type: TypeModalite, value: string): void {
          patchState(store, (state) => {
            if (!type || !value) return state;
            const oldValue: string = state.selectedOptionsRente.get(type);
            state.selectedOptionsRente = new Map(state.selectedOptionsRente).set(type, value);
            state.historiqueOptions.push({ type, value: oldValue });
            if (state.historiqueOptions.length > LIMITE_HISTORIQUE) {
              state.historiqueOptions.shift();
            }
            return state;
          });
        },

        precoChoixOptionPrecedent(): EntreeHistoriqueOption {
          const newHistorique: EntreeHistoriqueOption[] = [...store.historiqueOptions()];
          const lastValue: EntreeHistoriqueOption = newHistorique.pop();
          patchState(store, (state) => {
            if (!state.historiqueOptions.length) return state;
            const newState: RenteState = { historiqueOptions: newHistorique };
            newState.selectedOptionsRente = new Map(newState.selectedOptionsRente);
            if (lastValue) newState.selectedOptionsRente.set(lastValue.type, lastValue.value);
            return newState;
          });
          return lastValue;
        },

        precoChoixConsoPrecedent(): ChangementSimConso {
          const historiqueConso: ChangementSimConso[] = store.historiqueConso();
          const prevChangement: ChangementSimConso =
            historiqueConso?.length > 0 ? historiqueConso[historiqueConso.length - 1] : null;
          patchState(store, (state) => {
            if (!state.historiqueConso?.length) return state;
            const newHistorique: ChangementSimConso[] = [...(state.historiqueConso ?? [])];
            const lastValue: ChangementSimConso = newHistorique.pop();
            const newPrecoModifiee: PreconisationModifiee = { ...state.precoModifiee };
            switch (lastValue.champEnfant) {
              case SousChampConso.CAPITAL_PONCTUEL:
                newPrecoModifiee.consommationCapital = lastValue.value;
                break;
              case SousChampConso.RENTE:
                newPrecoModifiee.consommationRente = lastValue.value;
                break;
              case SousChampConso.REVENU_REGULIER_DURATION:
                newPrecoModifiee.dureeRppAnnuel = lastValue.value;
                break;
              case SousChampConso.REVENU_REGULIER_SUM:
                newPrecoModifiee.montantRppAnnuel = lastValue.value;
                break;
              case SousChampConso.RPP:
                newPrecoModifiee.consommationRpp = lastValue.value;
                break;
              case SousChampConso.SELECTION_RETENU_OPTION:
                newPrecoModifiee.renteRetenuOption = lastValue.value;
                break;
            }
            return {
              ...state,
              historiqueConso: newHistorique
            };
          });
          return prevChangement;
        },

        reinitListeOptions(): void {
          patchState(store, () => ({ selectedOptionsRente: new Map<TypeModalite, string>() }));
        },

        resetPrecoToOriginal(): void {
          patchState(store, (state) => ({
            precoModifiee: {
              ...state.precoModifiee,
              ...state.preconisation
            }
          }));
        },

        ajouterHistoriqueSimConso(changement: ChangementSimConso): void {
          patchState(store, (state) => {
            const newHistoConso: ChangementSimConso[] = [...(state.historiqueConso ?? []), changement];
            if (newHistoConso.length > 500) {
              newHistoConso.shift();
            }
            return { historiqueConso: newHistoConso };
          });
        },

        supprimerPreconisation(): void {
          patchState(store, () => ({
            preconisation: null as PreconisationInformationDTO,
            precoModifiee: null as PreconisationModifiee
          }));
        },

        loadPreconisation: (): Observable<PreconisationInformationDTO> =>
          preconisationService
            .getPreconisation(
              user.epargneCumulee(),
              dateEffet(),
              questionnaire.typeParcours(),
              questionnaire.getAllResponses()
            )
            .pipe(
              tapResponse({
                next: (preconisation) => {
                  const selectedOptionsRente: Map<TypeModalite, string> = preconisation.renteModalites.reduce(
                    (acc, value) => acc.set(value.type, value.code),
                    new Map<TypeModalite, string>()
                  );
                  questionnaire.setAffichageInfos({
                    afficher: questionnaire.affichageInfos()?.afficher ?? false,
                    current: 0,
                    ecrans: preconisation.universEcrans
                  });
                  patchState(store, () => ({
                    preconisation,
                    selectedOptionsRente,
                    historiqueConso: [] as ChangementSimConso[]
                  }));
                },
                error: (e) => errorStore.addError(e as Error, `Impossible de calculer la rente préconisée`)
              })
            ),

        setPreconisation(modification: PreconisationModifiee): void {
          patchState(store, () => ({ precoModifiee: modification }));
        },

        setBeneficiaire(beneficiaire: Beneficiaire, clauseCode: string = null): void {
          patchState(store, (state) => ({
            beneficiaire: beneficiaire ?? state.beneficiaire,
            clauseBeneficiaireCode: clauseCode ?? state.clauseBeneficiaireCode
          }));
        },

        createRenteSansOptResource: (
          signal: Signal<RenteSansOptSignalData> = computed(() => ({ renteSansOpt: store.renteSansOpt() }))
        ) =>
          rxResource({
            request: computed(() => ({
              codeSimu: tokenService.getSelectedSimuCode(),
              isUserInited: user.userInited(),
              ...signal?.()
            })),
            loader: (params) => {
              if (params.request.renteSansOpt) return of(params.request.renteSansOpt);
              return of(user.userInited()).pipe(
                filter(Boolean),
                switchMap(() =>
                  renteService
                    .renteCalcul(
                      params.request.dateEffet ?? dateEffet(),
                      user.epargneCumulee(),
                      params.request.consoCapital ?? store.consommationCapital()
                    )
                    .pipe(
                      tapResponse({
                        next: (renteSansOpt) => patchState(store, () => ({ renteSansOpt })),
                        error: (e) => errorStore.addError(e as Error, `Impossible de calculer la rente`)
                      })
                    )
                )
              );
            }
          }),

        createRenteAvecOptResource: (signal: Signal<string[]> = null) =>
          rxResource({
            request: signal ?? computed(() => Array.from(store.selectedOptionsRente().values())),
            loader: (params) => {
              return renteService
                .renteCalcul(dateEffet(), user.epargneCumulee(), store.consommationCapital(), true, params.request)
                .pipe(
                  tapResponse({
                    next: (renteAvecOpt) => patchState(store, () => ({ renteAvecOpt })),
                    error: (e) => errorStore.addError(e as Error, `Impossible de calculer la rente avec options`)
                  })
                );
            }
          })
      };
    }
  ),

  withHooks((store) => {
    const localStorageService = inject(LocalStorageService);

    return {
      onInit() {
        const state = localStorageService.getSavedState<RenteState>(StateKey.RENTE);
        if (state) store.retrieveState(state);
        effect(() => {
          const state: RenteState = getState(store);
          const savedState: SavedRenteState = {
            ...state,
            selectedOptionsRente: Object.fromEntries(state.selectedOptionsRente)
          };
          localStorageService.saveState(StateKey.RENTE, savedState);
        });
      }
    };
  })
);
